/**
 * AskWhyModal - Guided chat for Health Intelligence "Ask Why"
 * 
 * Opens with a prefilled question based on the current insight.
 * Calls backend for AI-generated response.
 * Tracks daily usage per tier limits.
 */

import React, { useState, useRef, useEffect } from 'react';
import { X, Send, Sparkles, Loader2 } from 'lucide-react';
import { brand } from '../../styles/brand';

const { colors, radius, spacing, typography } = brand;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AskWhyModal = ({
  show,
  onClose,
  prefillMessage = '',
  insightType = '',
  userId,
  userTier = 'free',
  isTrial = false,
  todayContext = {},
  userContext = {}
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `askwhy_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const hasSentPrefill = useRef(false);

  // Initialize with prefill on open
  useEffect(() => {
    if (show && prefillMessage && !hasSentPrefill.current) {
      hasSentPrefill.current = true;
      setMessages([]);
      setInput('');
      // Auto-send the prefilled question after a short delay
      const timer = setTimeout(() => sendMessage(prefillMessage), 400);
      return () => clearTimeout(timer);
    }
    if (!show) {
      hasSentPrefill.current = false;
    }
  }, [show, prefillMessage]);

  // Auto-scroll to bottom
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const sendMessage = async (messageText) => {
    const msg = messageText || input.trim();
    if (!msg || isLoading) return;

    const userMsg = { role: 'user', content: msg };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/health-intelligence/ask-why`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          question: msg,
          insight_type: insightType,
          session_id: sessionId,
          context: {
            calories: todayContext.calories || 0,
            calorie_goal: todayContext.calorieGoal || 2000,
            protein: todayContext.protein || 0,
            protein_goal: todayContext.proteinGoal || 150,
            fat: todayContext.fat || 0,
            fat_goal: todayContext.fatGoal || 65,
            carbs: todayContext.carbs || 0,
            carbs_goal: todayContext.carbsGoal || 250,
            diet_type: userContext.dietType || '',
            allergies: userContext.allergies || [],
            medical_conditions: userContext.medicalConditions || [],
            medications: userContext.medications || [],
            ancestry: userContext.ancestry || '',
            body_composition: userContext.bodyComposition || {},
            activity_level: userContext.activityLevel || '',
            primary_goal: userContext.primaryGoal || '',
            cuisine_preferences: userContext.cuisinePreferences || [],
            is_fasting: userContext.isFasting || false,
            meals_today: userContext.mealsToday || []
          }
        })
      });

      if (response.status === 429) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: 'You\'ve reached your daily Ask Why limit. Upgrade to Genius Plaite for unlimited questions.',
          isError: true
        }]);
        return;
      }

      if (!response.ok) {
        throw new Error(`Server error (${response.status})`);
      }

      const data = await response.json();
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: data.response || 'I couldn\'t generate a response. Please try again.'
      }]);
    } catch (err) {
      console.error('[AskWhy] Error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Something went wrong. Tap the retry button or type your question again.',
        isError: true,
        retryMsg: msg
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = (retryMsg) => {
    // Remove the error message and retry
    setMessages(prev => prev.filter(m => !m.isError || m.retryMsg !== retryMsg));
    sendMessage(retryMsg);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      zIndex: 10001,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{
        padding: '16px 20px',
        paddingTop: 'max(16px, env(safe-area-inset-top, 16px))',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Sparkles size={18} style={{ color: '#667eea' }} />
          <span style={{ fontSize: '16px', fontWeight: '700', color: 'white' }}>
            {insightType === 'smart_coach' ? 'MIQ Smart Coach' : 'Ask Why'}
          </span>
          <span style={{
            fontSize: '10px',
            background: 'rgba(102, 126, 234, 0.2)',
            color: '#667eea',
            padding: '2px 8px',
            borderRadius: '10px',
            fontWeight: '500'
          }}>
            {insightType === 'smart_coach' ? 'AI Health Intelligence' : 'Health Intelligence'}
          </span>
        </div>
        <button
          data-testid="ask-why-close"
          onClick={onClose}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.7)'
          }}
        >
          <X size={18} />
        </button>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          background: '#0d1117'
        }}
      >
        {/* Welcome hint */}
        {messages.length === 0 && !isLoading && (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: 'rgba(255,255,255,0.4)',
            fontSize: '13px'
          }}>
            <Sparkles size={24} style={{ color: '#667eea', marginBottom: '12px' }} />
            <p style={{ margin: 0 }}>
              {insightType === 'smart_coach'
                ? "Your personal health intelligence for nutrition, supplements, exercise, peptides, and health optimization. Ask me anything..."
                : "Ask me anything — what to eat at a restaurant, why your macros matter, or how to hit your goals..."}
            </p>
            {insightType === 'smart_coach' && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center', marginTop: '16px' }}>
                {[
                  "How does my ancestry affect my goals?",
                  "What am I missing in my supplement stack?",
                  "Optimize my peptide timing & dosing",
                  "Plan a workout around my limitations",
                  "What interactions should I know about?"
                ].map((q, i) => (
                  <button
                    key={i}
                    data-testid={`smart-coach-suggestion-${i}`}
                    onClick={() => {
                      setInput(q);
                    }}
                    style={{
                      padding: '6px 12px',
                      background: 'rgba(102, 126, 234, 0.1)',
                      border: '1px solid rgba(102, 126, 234, 0.2)',
                      borderRadius: '20px',
                      color: '#667eea',
                      fontSize: '11px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              gap: '8px'
            }}
          >
            {msg.role === 'assistant' && (
              <div style={{
                width: '28px',
                height: '28px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: '14px'
              }}>
                🧠
              </div>
            )}
            <div style={{
              maxWidth: '80%',
              padding: '10px 14px',
              borderRadius: msg.role === 'user'
                ? '16px 16px 4px 16px'
                : '16px 16px 16px 4px',
              background: msg.role === 'user'
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : msg.isError
                  ? 'rgba(244, 67, 54, 0.1)'
                  : 'rgba(255,255,255,0.06)',
              border: msg.role === 'user'
                ? 'none'
                : '1px solid rgba(255,255,255,0.08)',
              fontSize: '13px',
              color: msg.role === 'user' ? 'white' : msg.isError ? '#f44336' : 'rgba(255,255,255,0.85)',
              lineHeight: '1.5',
              whiteSpace: 'pre-wrap'
            }}>
              {msg.content}
              {msg.isError && msg.retryMsg && (
                <button
                  data-testid="ask-why-retry"
                  onClick={() => handleRetry(msg.retryMsg)}
                  style={{
                    display: 'block',
                    marginTop: '8px',
                    padding: '6px 14px',
                    background: 'rgba(102, 126, 234, 0.2)',
                    border: '1px solid rgba(102, 126, 234, 0.3)',
                    borderRadius: '12px',
                    color: '#667eea',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Retry
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px'
            }}>
              🧠
            </div>
            <div style={{
              padding: '10px 14px',
              borderRadius: '16px 16px 16px 4px',
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.08)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <Loader2 size={14} style={{ color: '#667eea', animation: 'spin 1s linear infinite' }} />
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>Analyzing...</span>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div style={{
        padding: '12px 16px',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-end'
      }}>
        <input
          ref={inputRef}
          data-testid="ask-why-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a follow-up..."
          style={{
            flex: 1,
            padding: '10px 14px',
            background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '20px',
            color: 'white',
            fontSize: '13px',
            outline: 'none',
            fontFamily: typography.fontFamily
          }}
        />
        <button
          data-testid="ask-why-send"
          onClick={() => sendMessage()}
          disabled={!input.trim() || isLoading}
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: input.trim() && !isLoading
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'rgba(255,255,255,0.05)',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: input.trim() && !isLoading ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s ease'
          }}
        >
          <Send size={16} style={{
            color: input.trim() && !isLoading ? 'white' : 'rgba(255,255,255,0.3)'
          }} />
        </button>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AskWhyModal;
